// This slice manages the overall state of the interview process.
// It tracks the current step, flow data, and validation results.
import {
  createSlice,
  createSelector,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "@/app/store";
import type {
  InterviewStepData,
  InterviewStep1Data,
  ValidateInterviewData,
} from "@/features/interview/types";

// Types
export type InterviewStep = 0 | 1;

// Discriminated union for step-specific data
export type StepData =
  | { step: 0; data: InterviewStepData }
  | { step: 1; data: InterviewStep1Data };

// Shape of the interview flow state in the Redux store
export interface InterviewFlowState {
  flowData: StepData | null;
  validationData: ValidateInterviewData | null;
}

const initialState: InterviewFlowState = {
  flowData: null,
  validationData: null,
};

// --- Redux Slice ---

// Redux slice handling interview state transitions and data updates.
export const interviewFlowSlice = createSlice({
  name: "interviewFlow",
  initialState,
  reducers: {
    // Updates one or more parts of the interview flow state.
    updateFlowState: (
      state,
      action: PayloadAction<
        Partial<InterviewFlowState> & { step?: InterviewStep }
      >,
    ) => {
      const { flowData, validationData, step } = action.payload;

      if (flowData !== undefined) state.flowData = flowData;

      if (validationData !== undefined) {
        state.validationData = validationData;
      }

      if (step !== undefined && state.flowData) {
        state.flowData.step = step;
      }
    },

    // Resets the entire interview flow to its initial state
    resetInterviewFlow: () => initialState,
  },
});

export const { updateFlowState, resetInterviewFlow } =
  interviewFlowSlice.actions;

export default interviewFlowSlice.reducer;

// --- Selectors ---

// Base selector for the interview flow state
export const selectInterviewFlowState = (state: RootState) =>
  state.interviewFlow;

// Returns the current step data
export const selectFlowData = createSelector(
  [selectInterviewFlowState],
  (s) => s.flowData,
);

// Retrieves the current step number (0, 1, or null if not set)
export const selectInterviewStep = createSelector(
  [selectFlowData],
  (flowData) => flowData?.step ?? null,
);

// Retrieves validation data results (e.g., from the initial validation check)
export const selectValidationData = createSelector(
  [selectInterviewFlowState],
  (s) => s.validationData,
);

// Helper selector to check if the interview flow has been properly initialized with data
export const selectIsInterviewFlowInitialized = createSelector(
  [selectFlowData],
  (flowData) => flowData !== null,
);

// --- Hooks ---

// Custom hook providing a simplified interface for components to interact with the interview flow.
// It combines state selection and action dispatching into a single API.
export const useInterviewFlowState = () => {
  const dispatch = useDispatch<AppDispatch>();

  const step = useSelector(selectInterviewStep);
  const flowData = useSelector(selectFlowData);
  const validationData = useSelector(selectValidationData);

  const setStepData = useCallback(
    (payload: StepData) => dispatch(updateFlowState({ flowData: payload })),
    [dispatch],
  );

  const setValidationData = useCallback(
    (payload: ValidateInterviewData | null) =>
      dispatch(updateFlowState({ validationData: payload })),
    [dispatch],
  );

  const goToStep = useCallback(
    (payload: InterviewStep) => dispatch(updateFlowState({ step: payload })),
    [dispatch],
  );

  const reset = useCallback(() => dispatch(resetInterviewFlow()), [dispatch]);

  return {
    step,
    flowData,
    validationData,
    setStepData,
    setValidationData,
    goToStep,
    resetInterviewFlow: reset,
  };
};
